import React from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../utils/styledComponent';
import theme from '../../theme';
import { formatClockTime } from '../../utils/displayFormat';

// Page-level "Refresh" control. Owns the visual layout for the
// label/timestamp pair plus the refresh button with spinning icon.
// State is owned by SupervisorDashboard so the button can sit above the
// filter bar even though the data fetches happen inside RouteOperationsTab.

const StyledDiv = styledComponent('div', {
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing.gapXl,

  '& .src-meta': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    lineHeight: theme.lineHeights.compact,
  },
  '& .src-meta-label': {
    ...theme.typography.microLabel,
    color: theme.colors.textMuted,
    textTransform: 'uppercase',
  },
  '& .src-meta-time': {
    color: theme.colors.textDark,
    fontWeight: theme.fontWeights.medium,
    fontSize: theme.sizes.smallFontSize,
    fontVariantNumeric: 'tabular-nums',
  },
  '& .src-btn': {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.gapMd,
    padding: theme.spacing.mediumButtonPadding,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.radii.input,
    background: theme.colors.bgCard,
    color: theme.colors.textDark,
    cursor: 'pointer',
    ...theme.typography.body,
    fontWeight: theme.fontWeights.medium,
    transition: theme.transitions.default,
  },
  '& .src-btn:hover:not(:disabled)': {
    background: theme.colors.bgMuted,
    borderColor: theme.colors.infoBlue,
  },
  '& .src-btn:disabled': {
    color: theme.colors.disabled,
    cursor: 'not-allowed',
  },
  '& .src-btn .src-icon': {
    fontSize: theme.sizes.iconLg,
    lineHeight: theme.lineHeights.tight,
    display: 'inline-block',
  },
  '& .src-btn.is-spinning .src-icon': {
    animation: 'src-refresh-spin 1s linear infinite',
  },
  '@keyframes src-refresh-spin': {
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' },
  },
});

function StatusRefreshControl({ isLoading, lastRefreshedAt, onRefresh }) {
  return (
    <StyledDiv>
      <div className="src-meta">
        <span className="src-meta-label">
          {isLoading ? 'Refreshing…' : 'Last refreshed'}
        </span>
        <span className="src-meta-time">
          {lastRefreshedAt
            ? formatClockTime(lastRefreshedAt, { seconds: true })
            : '—'}
        </span>
      </div>
      <button
        type="button"
        className={`src-btn ${isLoading ? 'is-spinning' : ''}`}
        onClick={onRefresh}
        disabled={isLoading}
        title={
          isLoading
            ? 'Refreshing board…'
            : 'Refresh routes, stop statuses, delivery windows, and supervisor actions'
        }
        data-testid="sd-refresh-statuses-btn"
      >
        <span className="src-icon" aria-hidden="true">
          ⟳
        </span>
        Refresh
      </button>
    </StyledDiv>
  );
}

StatusRefreshControl.propTypes = {
  isLoading: PropTypes.bool,
  lastRefreshedAt: PropTypes.instanceOf(Date),
  onRefresh: PropTypes.func.isRequired,
};

StatusRefreshControl.defaultProps = {
  isLoading: false,
  lastRefreshedAt: null,
};

export default StatusRefreshControl;
